import React, {Component, ReactNode, MouseEvent} from "react";
import {DateRange} from "util/time";
import {loadLandlordBookingsStats} from "services/bookings/landlords";
import {LandlordBookingStats} from "services/entities/booking";
import ProgressSpinner from "components/ProgressSpinner";
import './index.scss';
import {HelpIcon} from "components/Icon";
import {Fade, Popper} from "@material-ui/core";
import {
  BookingDurationIcon,
  BookingIcon, BookingsPerDealIcon,
  DealsIcon,
  RevenueIcon,
  RevenuePerBookingIcon,
  RevenuePerDealIcon,
  RevenuePerMonthIcon, RevenuePerWeekIcon
} from "components/Icon/Stats";
import {formatDecimal, formatPercentage} from "util/number";

interface RevenueStatsProps {
  dateRange: DateRange|null;
}

interface RevenueStatsState {
  stats: LandlordBookingStats|null;
  tooltip: {open: boolean, stat: StatDefinition|null, anchorEl: Element|null};
}

interface StatDefinition {
  title: string;
  tooltip: string;
  icon: ReactNode;
  renderValue: (stats: LandlordBookingStats)=>string;
}

export default class RevenueStats extends Component<RevenueStatsProps, RevenueStatsState> {
  static readonly STATS: StatDefinition[] = [
    { title: 'Total Revenue (incl. VAT)', tooltip: 'The gross value of all bookings from the start date of the booking.', icon: <RevenueIcon />, renderValue: stats => stats.revenue.total.format("$0") },
    { title: 'Total no. Deals', tooltip: 'A deal is one or more bookings made by one occupier in the same transaction.', icon: <DealsIcon />, renderValue: stats => stats.deals.count.toString() },
    { title: 'Total no. Bookings', tooltip: 'A booking is a block of time on one date.', icon: <BookingIcon />, renderValue: stats => stats.bookings.count.toString() },
    { title: 'Occupancy Rate', tooltip: 'Percentage of available hours that are booked.', icon: null, renderValue: stats => formatPercentage(stats.bookings.occupancyRate) },
    { title: 'Average Revenue / Month', tooltip: 'Average revenue taken per week over selected date range.', icon: <RevenuePerMonthIcon />, renderValue: stats => stats.revenue.averagePerMonth.format("$0") },
    { title: 'Average Revenue / Deal', tooltip: 'Average price paid per deal transaction.', icon: <RevenuePerDealIcon />, renderValue: stats => stats.revenue.averagePerDeal.format("$0") },
    { title: 'Average Revenue / Booking', tooltip: 'Average revenue per single booking across all deals.', icon: <RevenuePerBookingIcon />, renderValue: stats => stats.revenue.averagePerBooking.format("$0") },
    { title: 'Vacancy Rate', tooltip: 'Percentage of available hours that are not booked.', icon: null, renderValue: stats => formatPercentage(stats.bookings.vacancyRate) },
    { title: 'Average Revenue / Week', tooltip: 'Average revenue taken per week over selected data range.', icon: <RevenuePerWeekIcon />, renderValue: stats => stats.revenue.averagePerWeek.format("$0") },
    { title: 'Average Bookings / Deal', tooltip: 'Average number of single date bookings in one deal transaction.', icon: <BookingsPerDealIcon />, renderValue: stats => formatDecimal(stats.deals.averageBookings, 1) },
    { title: 'Average Booking Duration', tooltip: 'Average number of hours in one single booking.', icon: <BookingDurationIcon />, renderValue: stats => stats.bookings.averageDuration.format("H:mm") },
    { title: 'Total Hours Booked', tooltip: 'Total number of hours that have been booked in selected date range.', icon: null, renderValue: stats => Math.floor(stats.bookings.duration.totalHours).toString() },
  ];

  state: RevenueStatsState = {
    stats: null,
    tooltip: {open: false, stat: null, anchorEl: null}
  };

  componentDidMount() {
    if(this.props.dateRange) {
      this.loadStats(this.props.dateRange);
    }
  }

  componentDidUpdate(prevProps: Readonly<RevenueStatsProps>, prevState: Readonly<any>, snapshot?: any) {
    if(prevProps.dateRange !== this.props.dateRange && this.props.dateRange) {
      this.loadStats(this.props.dateRange);
    }
  }

  render() {
    const stats = this.state.stats;
    return <div className={`revenue-stats ${!stats ? 'loading' : ''}`}>
      { !stats && <ProgressSpinner /> }
      { stats && <div className="stats">
        { RevenueStats.STATS.map((s, i) => this.renderStat(s, stats, i)) }
      </div> }
      { this.renderTooltip() }
    </div>;
  }

  renderStat(statDef: StatDefinition, stats: LandlordBookingStats, index: number) {
    return <div key={index}>
      <h3>{ statDef.title }</h3>
      <button className="help" onMouseEnter={ev => this.onHelpMouseEnter(ev, statDef)}
                  onMouseLeave={this.onHelpMouseExit}><HelpIcon /></button>
      <hr />
      <div className="icon">{ statDef.icon }</div>
      <div className="content">{ statDef.renderValue(stats) }</div>
    </div>;
  }

  renderTooltip() {
    const {open, stat, anchorEl} = this.state.tooltip;
    return <Popper open={open} anchorEl={anchorEl} transition placement="right-start">
      {({TransitionProps}) => <Fade {...TransitionProps} timeout={350}>
        { stat ? <div className="revenue-stats-tooltip">
          <h4>{ stat.title }</h4>
          <hr />
          <span className="content">{ stat.tooltip }</span>
        </div> : <span /> }
      </Fade>}
    </Popper>;
  }

  onHelpMouseEnter = (ev: MouseEvent, stat: StatDefinition) => {
    this.setState({tooltip: {open: true, stat: stat, anchorEl: ev.currentTarget as Element|null}});
  };

  onHelpMouseExit = (ev: MouseEvent) => {
    const tooltip = this.state.tooltip;
    if(tooltip.anchorEl === ev.currentTarget) {
      this.setState({tooltip: {open: false, stat: tooltip.stat, anchorEl: tooltip.anchorEl}});
    }
  };

  loadStats(dateRange: DateRange) {
    loadLandlordBookingsStats(dateRange).then(
      stats => this.setState({stats: stats})
    );
  }
}